import React from 'react';
import PageHeader from '../components/layout/PageHeader';
import { pageDescriptions } from '../data/pageDescriptions';

const TermsPage = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });
  const copy = pageDescriptions['/terms'];
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <PageHeader
          title={copy.title}
          description={copy.description}
        />
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="p-6 space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                1. Acceptance of Terms
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  By accessing or using Resumemo (referred to as "the Service"), you acknowledge that you have read, understood, and agree to be bound by these Terms of Service. If you do not agree to these terms in their entirety, you are not authorized to use the Service.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                2. Description of Service
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  Resumemo provides AI-powered resume optimization, job application tracking, and career development tools designed to enhance your job search experience. Our features include:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Resume analysis and optimization using artificial intelligence</li>
                  <li>Personalized suggestions to improve your resume's effectiveness</li>
                  <li>Job application tracking and management</li>
                  <li>Career development resources and guidance</li>
                </ul>
                <p className="mt-3">
                  The Service may be modified, updated, or changed at any time without prior notice as we continuously improve our offerings.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                3. User Accounts and Registration
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  To access certain features of the Service, you must create an account. When registering, you agree to provide accurate, current, and complete information. You are solely responsible for:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Maintaining the confidentiality of your account credentials</li>
                  <li>All activities that occur under your account</li>
                  <li>Notifying us immediately of any unauthorized use of your account</li>
                </ul>
                <p className="mt-3">
                  We reserve the right to disable your account if we have reasonable grounds to believe you have violated these Terms.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                4. User Content and Data Rights
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  You retain all ownership rights to any content you submit to the Service, including resumes, cover letters, and personal information. By submitting content, you grant Resumemo a worldwide, non-exclusive, royalty-free license to use, store, copy, modify, and process this content solely for the purpose of:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Providing and improving the Service</li>
                  <li>Developing new features and functionality</li>
                  <li>Analyzing usage patterns to enhance user experience</li>
                  <li>Generating anonymized and aggregated statistics</li>
                </ul>
                <p className="mt-3">
                  We will not share your personal information with third parties for marketing purposes without your explicit consent.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                5. Privacy and Data Protection
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your personal information when you use the Service. By using Resumemo, you consent to the data practices described in our Privacy Policy.
                </p>
                <p className="mt-3">
                  We implement appropriate technical and organizational measures to protect your personal data against unauthorized or unlawful processing, accidental loss, destruction, or damage.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                6. Prohibited Uses
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  You agree not to misuse the Service or assist anyone else in doing so. Prohibited activities include, but are not limited to:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Attempting to interfere with, compromise, or disrupt the security or functionality of the Service</li>
                  <li>Using the Service for any illegal, fraudulent, or unauthorized purpose</li>
                  <li>Uploading viruses or malicious code, or conducting any form of network attack</li>
                  <li>Collecting or harvesting user information without proper consent</li>
                  <li>Creating multiple accounts for abusive or disruptive purposes</li>
                  <li>Accessing the Service through automated means without our prior permission</li>
                </ul>
                <p className="mt-3">
                  Violation of these prohibitions may result in termination of your access to the Service and potential legal action.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                7. Subscription and Billing
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  Resumemo offers both free and premium subscription plans. By selecting a premium plan, you agree to pay all fees associated with your chosen subscription. Unless otherwise stated:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Subscription fees are charged at the beginning of each billing period</li>
                  <li>Subscriptions automatically renew unless canceled before the renewal date</li>
                  <li>You may cancel your subscription at any time through your account settings</li>
                  <li>Refunds are provided in accordance with our Refund Policy</li>
                </ul>
                <p className="mt-3">
                  We reserve the right to change our subscription fees upon reasonable notice. Such notice will be provided via email or through the Service.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                8. Termination
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  We reserve the right to suspend or terminate your access to the Service at our sole discretion, without prior notice, for conduct that we believe violates these Terms, is harmful to other users, our business operations, or third parties.
                </p>
                <p className="mt-3">
                  Upon termination:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Your right to access and use the Service will immediately cease</li>
                  <li>We may delete or retain your account information and content as required by law or for legitimate business purposes</li>
                  <li>All provisions of these Terms which by their nature should survive termination shall survive</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                9. Disclaimer of Warranties
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>
                <p className="mt-3">
                  We do not guarantee that:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>The Service will be uninterrupted, timely, secure, or error-free</li>
                  <li>Results obtained from using the Service will be accurate or reliable</li>
                  <li>The quality of any products, services, information, or other material purchased or obtained through the Service will meet your expectations</li>
                  <li>Any errors in the Service will be corrected</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                10. Limitation of Liability
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, RESUMEMO, ITS AFFILIATES, OFFICERS, EMPLOYEES, AGENTS, PARTNERS, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, RESULTING FROM:
                </p>
                <ul className="list-disc pl-6 mt-3 space-y-2">
                  <li>Your access to or use of, or inability to access or use, the Service</li>
                  <li>Any conduct or content of any third party on the Service</li>
                  <li>Any content obtained from the Service</li>
                  <li>Unauthorized access, use, or alteration of your transmissions or content</li>
                </ul>
                <p className="mt-3">
                  IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS EXCEED THE AMOUNT PAID BY YOU, IF ANY, FOR ACCESSING THE SERVICE DURING THE TWELVE (12) MONTHS PRIOR TO THE CLAIM.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                11. Changes to Terms
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  We reserve the right to modify these Terms at any time at our sole discretion. When we make material changes, we will notify you via email or through a prominent notice on our Service prior to the changes becoming effective. Your continued use of the Service after such changes constitutes your acceptance of the new Terms.
                </p>
                <p className="mt-3">
                  It is your responsibility to review these Terms periodically for any updates or changes.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                12. Governing Law and Dispute Resolution
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  These Terms shall be governed by and construed in accordance with the laws of the state of [Your State], without regard to its conflict of law principles. Any dispute arising from or relating to these Terms or your use of the Service shall be resolved exclusively through binding arbitration in accordance with the rules of the American Arbitration Association.
                </p>
                <p className="mt-3">
                  You agree to submit to the personal jurisdiction of the courts located within [Your County], [Your State] for the purpose of litigating all such claims or disputes.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-800 border-b border-gray-200 pb-2">
                13. Contact Information
              </h2>
              <div className="mt-4 text-gray-600 leading-relaxed">
                <p>
                  If you have any questions, concerns, or feedback regarding these Terms of Service, please contact our support team at:
                </p>
                <div className="mt-3 bg-gray-100 p-4 rounded-md inline-block">
                  <p className="font-medium text-blue-600">admin@myresumemo.com</p>
                </div>
                <p className="mt-3">
                  We strive to respond to all inquiries within 2 business days.
                </p>
              </div>
            </section>

            <div className="mt-10 pt-6 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-500">
                Last updated: {currentDate}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;